import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { QuizTemplateManager } from '../components/QuizTemplateManager';
import { formatDate } from '../lib/utils';

const box = { background: '#161d31', borderRadius: 12, padding: 16, marginBottom: 16 };
const btn = { background: '#4f46e5', color: 'white', border: 0, borderRadius: 8, padding: '10px 14px', cursor: 'pointer', fontSize: 14 };
const linkBtn = { ...btn, textDecoration: 'none', display: 'inline-block' };
const dangerBtn = { ...btn, background: '#dc2626' };
const badgeBase = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '5px 10px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
  border: '1px solid transparent',
};

function modeBadge(quiz) {
  if (quiz.generated_template_type === 'rule') return { text: 'Generated quiz', style: { background: '#312e81', color: '#c7d2fe', borderColor: '#4f46e5' } };
  if (quiz.generated_template_type === 'fixed') return { text: 'From fixed template', style: { background: '#0f766e', color: '#ccfbf1', borderColor: '#14b8a6' } };
  return { text: 'Manual quiz', style: { background: '#1f2937', color: '#cbd5e1', borderColor: '#475569' } };
}

export default function QuizDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [items, setItems] = useState([]);
  const [questionsById, setQuestionsById] = useState({});
  const [categories, setCategories] = useState([]);
  const [vtneDomains, setVtneDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadQuiz();
  }, [id]);

  async function loadQuiz() {
    try {
      const [quizData, itemsData, questionRows, categoriesData, domainsData] = await Promise.all([
        api.quiz(id),
        api.quizItems(id),
        api.questions(),
        api.categories(),
        api.vtneDomains(),
      ]);
      setQuiz(quizData);
      setItems(itemsData);
      setQuestionsById(Object.fromEntries(questionRows.map(question => [question.id, question])));
      setCategories(categoriesData);
      setVtneDomains(domainsData);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Delete quiz "${quiz.title}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.deleteQuiz(id);
      navigate('/quizzes');
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  }

  if (loading) return <div style={{ padding: 20 }}>Loading quiz...</div>;
  if (error) return <div style={{ padding: 20, color: '#fca5a5' }}>Error: {error}</div>;
  if (!quiz) return <div style={{ padding: 20 }}>Quiz not found</div>;

  const hasQuestions = items.length > 0;
  const canExport = hasQuestions && quiz.status !== 'draft';
  const hasSnapshots = (quiz.snapshot_count || 0) > 0;
  const mode = modeBadge(quiz);

  return (
    <div style={{ padding: 20, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: '0 0 8px 0' }}>{quiz.title}</h1>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            <span style={{ ...badgeBase, ...mode.style }}>{mode.text}</span>
            {hasSnapshots && (
              <span style={{ ...badgeBase, background: '#14532d', color: '#bbf7d0', borderColor: '#16a34a' }}>
                Frozen snapshots · {quiz.snapshot_count}
              </span>
            )}
          </div>
          <p style={{ margin: 0, color: '#94a3b8', fontSize: 14 }}>Status: {quiz.status} | Updated: {formatDate(quiz.updated_at)}</p>
          {quiz.generated_template_name && (
            <p style={{ margin: '8px 0 0 0', color: '#cbd5e1', fontSize: 14 }}>
              Source template: <strong>{quiz.generated_template_name}</strong>
            </p>
          )}
          {quiz.description && <p style={{ margin: '8px 0 0 0', color: '#cbd5e1', fontSize: 14 }}>{quiz.description}</p>}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Link to={`/quizzes/${id}/edit`} style={{ ...linkBtn }}>Edit</Link>
          {hasQuestions && <Link to={`/quizzes/${id}/export`} style={{ ...linkBtn }}>Export</Link>}
          <button
            onClick={handleDelete}
            disabled={deleting}
            style={{ ...dangerBtn, cursor: deleting ? 'wait' : 'pointer' }}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>

      {hasSnapshots && (
        <div style={{ ...box, background: '#052e16', borderLeft: '4px solid #22c55e' }}>
          <p style={{ margin: 0, fontWeight: 600 }}>Exports use frozen content for this quiz.</p>
          <p style={{ margin: '6px 0 0 0', color: '#dcfce7', fontSize: 14 }}>
            This quiz has {quiz.snapshot_count} snapshot{quiz.snapshot_count === 1 ? '' : 's'}. GIFT and PDF exports use the frozen snapshot content even if the source questions change later.
          </p>
        </div>
      )}

      {!hasQuestions && (
        <div style={{ ...box, background: '#152e8d', borderLeft: '4px solid #3b82f6' }}>
          <p style={{ margin: '0 0 12px 0', fontWeight: 500 }}>No questions added yet</p>
          <p style={{ margin: 0, color: '#cbd5e1', fontSize: 14 }}>Add at least one question to edit and export the quiz.</p>
          <Link to={`/quizzes/${id}/edit`} style={{ ...btn, marginTop: 12, display: 'inline-block', padding: '8px 12px', fontSize: 13 }}>+ Add Questions</Link>
        </div>
      )}

      {hasQuestions && !canExport && (
        <div style={{ ...box, background: '#78350f', borderLeft: '4px solid #f59e0b' }}>
          <p style={{ margin: 0, fontWeight: 500 }}>⚠ Quiz is in draft mode</p>
          <p style={{ margin: '4px 0 0 0', color: '#cbd5e1', fontSize: 14 }}>Change status to "active" or "archived" before exporting.</p>
        </div>
      )}

      <QuizTemplateManager quiz={quiz} categories={categories} vtneDomains={vtneDomains} />

      <div style={box}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0 }}>Questions ({items.length})</h2>
          {hasQuestions && <Link to={`/quizzes/${id}/edit`} style={{ color: '#93c5fd', textDecoration: 'none', fontSize: 14 }}>+ Add more</Link>}
        </div>

        {items.length === 0 ? (
          <p style={{ color: '#94a3b8', margin: 0 }}>No questions added yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {items.map((item, idx) => {
              const question = questionsById[item.question_id];
              return (
                <div key={item.id} style={{ padding: 12, background: '#0f172a', borderRadius: 6, border: '1px solid #334155' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <span style={{ color: '#64748b', marginRight: 8 }}>#{idx + 1}</span>
                      <strong style={{ fontSize: 15 }}>{question?.admin_title || 'Question ' + item.question_id}</strong>
                      {question?.stem && <p style={{ margin: '8px 0 0 0', color: '#cbd5e1', fontSize: 13, whiteSpace: 'pre-wrap' }}>{question.stem.substring(0, 120)}{question.stem.length > 120 ? '…' : ''}</p>}
                    </div>
                    <Link to={`/quizzes/${id}/edit`} style={{ color: '#93c5fd', textDecoration: 'none', fontSize: 12, flexShrink: 0 }}>Edit list</Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
